/**
 * useClientSummary — carrega TUDO que um cliente já movimentou no app.
 *
 * Sete tabelas têm `client_id` (`quotes`, `service_orders`, `contracts`,
 * `receipts`, `appointments`, `nfse_documents`, `finance_entries`) e até aqui o
 * histórico do cliente lia só a primeira. Este hook busca as sete EM PARALELO
 * (`Promise.all`) numa única query do TanStack, para a tela ter um só estado de
 * carregamento em vez de sete cascatas.
 *
 * Três decisões que não são óbvias:
 *
 * 1. **Filtro duplo `client_id` + `company_id`.** A RLS já isola a empresa, mas
 *    `quotes` tem policies públicas de compartilhamento (`/o/:id`) e o cache
 *    local é namespaceado pela empresa ativa; filtrar pelos dois é consistência
 *    com o resto do app e defesa em profundidade. Também garante que trocar de
 *    empresa nunca mostre documento da anterior — a `queryKey` carrega o
 *    `companyId`.
 * 2. **Offline-first de verdade.** `initialData` monta o resumo a partir dos
 *    caches que o app já mantém (aquecidos pelo `OfflineDataCacheWarmup`),
 *    filtrando por `client_id` em memória, e `networkMode: 'offlineFirst'` deixa
 *    a query rodar mesmo sem rede em vez de ficar pausada. Sem rede a página
 *    abre com dados; com rede ela revalida.
 * 3. **Gate de PAPEL antes de consultar o financeiro.** `finance_entries` é
 *    owner/admin-only na RLS: para um `member` a consulta voltaria VAZIA e a
 *    tela mostraria "R$ 0,00 a receber", que é falso. Quando `canSeeFinance` é
 *    falso nem consultamos, e devolvemos `financeEntries: null` — o agregador
 *    traduz isso em `finance: null` e a tela ESCONDE o bloco.
 *
 * NFS-e é a única origem sem cache offline (o módulo fiscal é online-only por
 * decisão de produto), então offline ela simplesmente não aparece na linha do
 * tempo — nunca com número errado.
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useRole } from '@/hooks/useRole';
import { supabase } from '@/integrations/supabase/client';
import {
  getCachedAppointments,
  getCachedContracts,
  getCachedFinanceEntries,
  getCachedQuotes,
  getCachedReceipts,
  getCachedServiceOrders,
} from '@/lib/offlineDataCache';
import type { ClientSummarySources } from '@/lib/clientSummary';

const SUMMARY_KEY = 'client-summary';

/** Linhas cacheadas daquele cliente, ou `[]` quando não há cache. */
function fromCache(rows: any[] | null, clientId: string): any[] {
  return (rows ?? []).filter((row) => row?.client_id === clientId);
}

/**
 * Monta as origens a partir SÓ dos caches locais. Usado como `initialData` (a
 * tela pinta na hora) e como rede de segurança quando a busca no servidor falha
 * — mostrar o último snapshot conhecido é melhor que uma tela vazia.
 */
function sourcesFromCache(
  userId: string,
  clientId: string,
  canSeeFinance: boolean,
): ClientSummarySources {
  return {
    quotes: fromCache(getCachedQuotes(userId), clientId),
    serviceOrders: fromCache(getCachedServiceOrders(userId), clientId),
    contracts: fromCache(getCachedContracts(userId), clientId),
    receipts: fromCache(getCachedReceipts(userId), clientId),
    appointments: fromCache(getCachedAppointments(userId), clientId),
    // Sem cache offline: o módulo fiscal é online-only.
    nfseDocuments: [],
    financeEntries: canSeeFinance ? fromCache(getCachedFinanceEntries(userId), clientId) : null,
  };
}

export function useClientSummary(clientId?: string | null) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { canSeeFinance } = useRole();
  const companyId = activeCompany?.id ?? null;
  const userId = user?.id ?? null;

  const query = useQuery({
    // `canSeeFinance` entra na chave: se o papel mudar (troca de empresa), o
    // resumo não pode reaproveitar um cache montado sob outra permissão.
    queryKey: [SUMMARY_KEY, companyId, clientId, canSeeFinance],
    enabled: Boolean(userId && companyId && clientId),
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    // `initialData` conta como dado FRESCO para o TanStack, então com
    // `staleTime` a query não revalidaria na montagem e o resumo mostraria o
    // cache velho por 60s — exatamente a armadilha #11 do CLAUDE.md. Espelha o
    // que `useContracts` faz: offline-first ainda serve o cache na hora, e
    // offline nem tenta ir à rede.
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () =>
      userId && clientId ? sourcesFromCache(userId, clientId, canSeeFinance) : undefined,
    queryFn: async (): Promise<ClientSummarySources> => {
      if (!userId || !companyId || !clientId) {
        return { quotes: [], financeEntries: canSeeFinance ? [] : null };
      }

      const scoped = (table: string, columns: string) =>
        supabase
          .from(table as any)
          .select(columns)
          .eq('client_id', clientId)
          .eq('company_id', companyId);

      /**
       * Sem rede, o cliente do Supabase REJEITA (não devolve `{ error }`), e um
       * `Promise.all` com uma rejeição descarta as outras seis respostas — o
       * fallback por origem abaixo nunca rodaria e a página cairia em estado de
       * erro. Neutralizando a rejeição aqui, cada origem decide sozinha se usa o
       * servidor ou o cache.
       */
      const safe = <T,>(p: PromiseLike<T>): Promise<T | { data: null; error: true }> =>
        Promise.resolve(p).then(
          (r) => r,
          () => ({ data: null, error: true as const }),
        );

      const [
        quotes,
        serviceOrders,
        contracts,
        receipts,
        appointments,
        nfseDocuments,
        financeEntries,
      ] = await Promise.all([
        safe(scoped('quotes', 'id, title, total, status, created_at, document_type')),
        safe(
          scoped('service_orders', 'id, title, total, status, created_at, scheduled_date, sequence_id'),
        ),
        safe(scoped('contracts', 'id, title, total, status, created_at, sequence_id')),
        safe(
          scoped(
            'receipts',
            'id, description, amount, issue_date, created_at, cancelled_at, sequence_id',
          ),
        ),
        safe(scoped('appointments', 'id, title, status, starts_at, created_at, all_day')),
        safe(
          scoped('nfse_documents', 'id, numero, status, valor_servico, emitida_em, created_at').is(
            'discarded_at',
            null,
          ),
        ),
        // Gate de PAPEL: nem consulta quando o usuário não pode ler o financeiro.
        canSeeFinance
          ? safe(
              scoped(
                'finance_entries_with_status',
                'id, description, amount, kind, status, computed_status, due_date, paid_date',
              ),
            )
          : Promise.resolve({ data: null, error: null }),
      ]);

      // Uma origem que falhar (RLS inesperada, tabela indisponível) não pode
      // derrubar o resumo inteiro: cai para o cache daquela origem e as outras
      // seis continuam corretas.
      const pick = (result: any, cached: any[]): any[] =>
        result?.error ? cached : ((result?.data ?? []) as any[]);

      return {
        quotes: pick(quotes, fromCache(getCachedQuotes(userId), clientId)),
        serviceOrders: pick(serviceOrders, fromCache(getCachedServiceOrders(userId), clientId)),
        contracts: pick(contracts, fromCache(getCachedContracts(userId), clientId)),
        receipts: pick(receipts, fromCache(getCachedReceipts(userId), clientId)),
        appointments: pick(appointments, fromCache(getCachedAppointments(userId), clientId)),
        nfseDocuments: pick(nfseDocuments, []),
        financeEntries: canSeeFinance
          ? pick(financeEntries, fromCache(getCachedFinanceEntries(userId), clientId))
          : null,
      };
    },
  });

  return {
    ...query,
    sources: query.data ?? { financeEntries: canSeeFinance ? [] : null },
    canSeeFinance,
  };
}
